/**
 * Sound Manager
 * Manages notification sounds with volume control, enable/disable functionality, and sound selection
 */

export type NotificationType = 'approved' | 'rejected' | 'assigned' | 'general';
export type SoundVariant = 'variant1' | 'variant2' | 'variant3';

export interface SoundSettings {
  enabled: boolean;
  volume: number; // 0-100
  selectedSounds: Record<NotificationType, SoundVariant>;
  enabledTypes: Record<NotificationType, boolean>; // per-type sound toggle
}

// Sound variants for each notification type
// Using CDN URLs from Mixkit (https://mixkit.co/free-sound-effects/)
const SOUND_VARIANTS: Record<NotificationType, Record<SoundVariant, { name: string; file: string }>> = {
  approved: {
    variant1: { name: 'Chime', file: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3' },
    variant2: { name: 'Bell', file: 'https://assets.mixkit.co/active_storage/sfx/2870/2870-preview.mp3' },
    variant3: { name: 'Success', file: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3' },
  },
  rejected: {
    variant1: { name: 'Alert', file: 'https://assets.mixkit.co/active_storage/sfx/2871/2871-preview.mp3' },
    variant2: { name: 'Buzzer', file: 'https://assets.mixkit.co/active_storage/sfx/2872/2872-preview.mp3' },
    variant3: { name: 'Error', file: 'https://assets.mixkit.co/active_storage/sfx/2955/2955-preview.mp3' },
  },
  assigned: {
    variant1: { name: 'Notification', file: 'https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3' },
    variant2: { name: 'Ding', file: 'https://assets.mixkit.co/active_storage/sfx/2353/2353-preview.mp3' },
    variant3: { name: 'Pop', file: 'https://assets.mixkit.co/active_storage/sfx/2357/2357-preview.mp3' },
  },
  general: {
    variant1: { name: 'Notification', file: 'https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3' },
    variant2: { name: 'Beep', file: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3' },
    variant3: { name: 'Chime', file: 'https://assets.mixkit.co/active_storage/sfx/2870/2870-preview.mp3' },
  },
};

const STORAGE_KEY = 'notification_sound_settings';

class SoundManager {
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private settings: SoundSettings;

  constructor() {
    // Load settings from localStorage
    this.settings = this.loadSettings();
    
    // Preload all sound files
    this.preloadSounds();
  }

  /**
   * Load sound settings from localStorage
   */
  private loadSettings(): SoundSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Ensure selectedSounds exists
        if (!parsed.selectedSounds) {
          parsed.selectedSounds = {
            approved: 'variant1',
            rejected: 'variant1',
            assigned: 'variant1',
            general: 'variant1',
          };
        }
        // Ensure enabledTypes exists (backward compat)
        if (!parsed.enabledTypes) {
          parsed.enabledTypes = {
            approved: true,
            rejected: true,
            assigned: true,
            general: true,
          };
        }
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load sound settings:', error);
    }
    
    // Default settings
    return {
      enabled: true,
      volume: 70,
      selectedSounds: {
        approved: 'variant1',
        rejected: 'variant1',
        assigned: 'variant1',
        general: 'variant1',
      },
      enabledTypes: {
        approved: true,
        rejected: true,
        assigned: true,
        general: true,
      },
    };
  }

  /**
   * Save sound settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save sound settings:', error);
    }
  }

  /**
   * Preload all sound files
   */
  private preloadSounds(): void {
    Object.entries(SOUND_VARIANTS).forEach(([type, variants]) => {
      Object.entries(variants).forEach(([variant, { file }]) => {
        const key = `${type}-${variant}`;
        const audio = new Audio(file);
        audio.preload = 'auto';
        this.audioElements.set(key, audio);
      });
    });
  }

  /**
   * Get sound file path for a notification type
   */
  private getSoundFile(type: NotificationType): string {
    const variant = this.settings.selectedSounds[type] || 'variant1';
    return SOUND_VARIANTS[type][variant].file;
  }

  /**
   * Play notification sound
   */
  play(type: NotificationType): void {
    if (!this.settings.enabled) {
      return;
    }
    // Check per-type toggle
    if (this.settings.enabledTypes && this.settings.enabledTypes[type] === false) {
      return;
    }

    const variant = this.settings.selectedSounds[type] || 'variant1';
    const key = `${type}-${variant}`;
    const audio = this.audioElements.get(key);
    
    if (!audio) {
      console.warn(`Sound not found for type: ${type}, variant: ${variant}`);
      return;
    }

    try {
      // Clone the audio element to allow multiple simultaneous plays
      const clone = audio.cloneNode() as HTMLAudioElement;
      clone.volume = this.settings.volume / 100;
      
      // Play the sound
      clone.play().catch((error) => {
        console.error('Failed to play sound:', error);
      });
    } catch (error) {
      console.error('Failed to play sound:', error);
    }
  }

  /**
   * Get current sound settings
   */
  getSettings(): SoundSettings {
    return { ...this.settings, selectedSounds: { ...this.settings.selectedSounds } };
  }

  /**
   * Update sound settings
   */
  updateSettings(settings: Partial<SoundSettings>): void {
    this.settings = {
      ...this.settings,
      ...settings,
      selectedSounds: {
        ...this.settings.selectedSounds,
        ...(settings.selectedSounds || {}),
      },
      enabledTypes: {
        ...this.settings.enabledTypes,
        ...(settings.enabledTypes || {}),
      },
    };
    this.saveSettings();
  }

  /**
   * Enable/disable sound for a specific notification type
   */
  setTypeEnabled(type: NotificationType, enabled: boolean): void {
    this.updateSettings({
      enabledTypes: {
        ...this.settings.enabledTypes,
        [type]: enabled,
      },
    });
  }

  /**
   * Get enabled state for a specific notification type
   */
  isTypeEnabled(type: NotificationType): boolean {
    return this.settings.enabledTypes?.[type] !== false;
  }

  /**
   * Enable/disable sounds
   */
  setEnabled(enabled: boolean): void {
    this.updateSettings({ enabled });
  }

  /**
   * Set volume (0-100)
   */
  setVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    this.updateSettings({ volume: clampedVolume });
  }

  /**
   * Set sound variant for a notification type
   */
  setSound(type: NotificationType, variant: SoundVariant): void {
    this.updateSettings({
      selectedSounds: {
        ...this.settings.selectedSounds,
        [type]: variant,
      },
    });
  }

  /**
   * Get available sound variants for a notification type
   */
  getAvailableVariants(type: NotificationType): Array<{ value: SoundVariant; label: string }> {
    return Object.entries(SOUND_VARIANTS[type]).map(([variant, { name }]) => ({
      value: variant as SoundVariant,
      label: name,
    }));
  }

  /**
   * Test play a sound (for settings preview)
   */
  testPlay(type: NotificationType, variant?: SoundVariant): void {
    // Temporarily enable sound for testing
    const wasEnabled = this.settings.enabled;
    const originalVariant = this.settings.selectedSounds[type];
    
    this.settings.enabled = true;
    if (variant) {
      this.settings.selectedSounds[type] = variant;
    }
    
    this.play(type);
    
    this.settings.enabled = wasEnabled;
    this.settings.selectedSounds[type] = originalVariant;
  }
}

// Export singleton instance
export const soundManager = new SoundManager();

// Export sound variants for UI
export { SOUND_VARIANTS };
